import { Vehicle, Booking, VehicleType, AvailabilityWindow } from './types';

export interface TimeSegment {
  startTime: Date;
  endTime: Date;
  occupiedCount: number;
  totalCount: number;
}

export interface VehicleTypeGroup {
  vehicleType: VehicleType;
  vehicles: Vehicle[];
  totalCount: number;
  availableCount: number;
  warningCount: number;
  unavailableCount: number;
  timeSegments: TimeSegment[];
  isExpanded: boolean;
}

/**
 * Groups vehicles by type
 */
export function groupVehiclesByType(vehicles: Vehicle[]): Map<VehicleType, Vehicle[]> {
  const groups = new Map<VehicleType, Vehicle[]>();
  
  vehicles.forEach(vehicle => {
    const existing = groups.get(vehicle.vehicleType) || [];
    groups.set(vehicle.vehicleType, [...existing, vehicle]);
  });
  
  return groups;
}

/**
 * Calculate aggregated timeline segments for a vehicle type
 * This shows how many vehicles are busy at each time period
 */
export function calculateTimeSegments(
  vehicles: Vehicle[],
  bookings: Booking[],
  selectedDate: Date,
  startDate?: Date
): TimeSegment[] {
  // Use startDate if provided (for continuous timeline), otherwise use selectedDate
  const dayStart = startDate || (() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  
  const dayEnd = new Date(dayStart);
  // For continuous timeline, show 7 days
  dayEnd.setDate(dayEnd.getDate() + (startDate ? 7 : 1));
  
  // Collect all unique time points from bookings for this vehicle type
  const timePoints = new Set<number>();
  timePoints.add(dayStart.getTime());
  timePoints.add(dayEnd.getTime());
  
  const vehicleIds = new Set(vehicles.map(v => v.id));
  const relevantBookings = bookings.filter(b => vehicleIds.has(b.vehicleId));
  
  relevantBookings.forEach(booking => {
    timePoints.add(booking.startTime.getTime());
    timePoints.add(booking.endTime.getTime());
  });
  
  // Sort time points
  const sortedTimes = Array.from(timePoints).sort((a, b) => a - b);
  
  // Calculate occupancy for each segment
  const segments: TimeSegment[] = [];
  
  for (let i = 0; i < sortedTimes.length - 1; i++) {
    const segmentStart = new Date(sortedTimes[i]);
    const segmentEnd = new Date(sortedTimes[i + 1]);
    
    // Count how many vehicles are occupied during this segment
    let occupiedCount = 0;
    
    relevantBookings.forEach(booking => {
      // Check if this booking overlaps with the segment
      if (booking.startTime < segmentEnd && booking.endTime > segmentStart) {
        occupiedCount++;
      }
    });
    
    segments.push({
      startTime: segmentStart,
      endTime: segmentEnd,
      occupiedCount,
      totalCount: vehicles.length
    });
  }
  
  return segments;
}

/**
 * Calculate availability summary for a vehicle type within a specific window
 */
export function calculateAvailabilitySummary(
  vehicleFeasibilities: any[],
  availabilityWindow: AvailabilityWindow | null
): { available: number; warning: number; unavailable: number } {
  if (!availabilityWindow) {
    return { available: 0, warning: 0, unavailable: 0 };
  }
  
  let available = 0;
  let warning = 0;
  let unavailable = 0;
  
  vehicleFeasibilities.forEach(feasibility => {
    if (!feasibility) return;
    
    switch (feasibility.status) {
      case 'available':
        available++;
        break;
      case 'warning':
        warning++;
        break;
      case 'unavailable':
        unavailable++;
        break;
    }
  });
  
  return { available, warning, unavailable };
}

/**
 * Format availability text for display
 */
export function formatAvailabilityText(
  available: number,
  warning: number,
  unavailable: number,
  hasWindow: boolean
): string {
  if (!hasWindow) {
    return '';
  }
  
  if (available > 0 && warning === 0) {
    return `${available} available`;
  } else if (available > 0 && warning > 0) {
    return `${available} available, ${warning} with warning`;
  } else if (warning > 0 && available === 0) {
    return `${warning} available (tight turnaround)`;
  } else {
    return 'Not available – location constraint';
  }
}